import { createFileRoute } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { Sparkles, Languages, Target, Users, Send, LineChart, Bookmark } from "lucide-react";
import { AiGeneratorPage } from "@/components/marketing/AiGeneratorPage";

export const Route = createFileRoute("/vi/ai/email-writer")({
  head: () => ({
    meta: [
      { title: "Trợ lý viết email AI — Banana Cards" },
      { name: "description", content: "Viết email bán hàng và theo dõi khách hàng bằng AI." },
    ],
    links: alternateLinkList("/ai/email-writer", "vi"),
  }),
  component: Page,
});

const EXAMPLES = [
  "Email cold cho một founder SaaS giới thiệu dịch vụ audit UX của tôi, giọng thân thiện, CTA = gọi 15 phút",
  "Email theo dõi khách chưa phản hồi sau buổi demo tuần trước",
  "Email nhắc lại cho khách hàng 60 ngày chưa đăng nhập",
  "Email networking cho người tôi gặp ở hội thảo hôm qua — ngắn gọn và ấm áp",
  "Email nhắc lịch sự về hoá đơn quá hạn 14 ngày",
  "Email chào mừng người mới đăng ký nhận bản tin, giọng vui tươi",
];

export function Page() {
  return (
    <AiGeneratorPage
      eyebrow="Trợ lý viết email AI"
      title="Email phù hợp,"
      italic="mọi lúc."
      description="Email cold, theo dõi, chào mừng, nhắc nhở — Banana viết đúng tông giọng của bạn, hơn 50 ngôn ngữ, kèm CTA rõ ràng và tiêu đề thực sự được mở."
      headline="Bạn muốn viết loại email nào?"
      placeholders={EXAMPLES}
      linkedFromHref="/vi/platform/networking/welcome-email"
      linkedFromLabel="Banana — Email & tiếp cận khách hàng"
      sections={[
        { icon: Sparkles, title: "Đúng giọng, đúng thương hiệu", desc: "Banana học giọng văn của bạn từ email trước đó nên bản nháp nghe giống bạn viết, không phải mẫu chung." },
        { icon: Target, title: "Xoay quanh một CTA", desc: "Mỗi bản nháp thiết kế xoay quanh một hành động duy nhất — đặt lịch, trả lời, click, thanh toán." },
        { icon: Languages, title: "Hơn 50 ngôn ngữ", desc: "Viết hoặc dịch sang tiếng Anh, Tây Ban Nha, Pháp, Đức, Trung, Nhật và nhiều hơn nữa." },
      ]}
      capabilities={[
        "Cold, theo dõi, chào mừng, nhắc nhở, kích hoạt lại",
        "Cài đặt tông giọng (thân thiện, trang trọng, vui vẻ, thẳng thắn)",
        "Biến thể tiêu đề (sẵn sàng A/B test)",
        "Cá nhân hoá từ dữ liệu contact + công ty",
        "Biến như {{ho_ten}} và {{cong_ty}}",
        "Tự động dịch sang hơn 50 ngôn ngữ",
        "Gửi trực tiếp từ Gmail hoặc Outlook",
        "Theo dõi mở, click và phản hồi",
      ]}
      howItWorks={[
        { title: "Mô tả email", desc: "Đối tượng, mục tiêu, tông giọng — một câu là đủ." },
        { title: "AI soạn tiêu đề + nội dung", desc: "Nhiều biến thể tiêu đề kèm bản nháp gọn gàng." },
        { title: "Chỉnh 1 chạm", desc: "Ngắn hơn, thân thiện hơn, gấp gáp hơn — Banana viết lại tức thì." },
        { title: "Gửi & theo dõi", desc: "Gửi từ inbox của bạn; chúng tôi theo dõi mở, click và phản hồi." },
      ]}
      faq={[
        { q: "Email có thực sự giống giọng của tôi không, hay giống robot?", a: "Có — Banana huấn luyện giọng nói chung cho workspace từ email trước đó (tông, độ dài câu, cách chào cuối, cụm từ hay dùng). Mỗi thành viên có thể thêm giọng riêng của mình. Kết quả là bản nháp nghe như bạn viết, không phải AI chung chung." },
        { q: "AI có viết được tiêu đề khiến người ta mở không?", a: "Có — mỗi bản nháp đi kèm 3 biến thể tiêu đề tối ưu tỷ lệ mở, cộng thêm snippet xem trước. Bạn có thể A/B test tự động khi gửi chuỗi email." },
        { q: "Tôi có thể gửi trực tiếp từ inbox của mình không?", a: "Có — kết nối Gmail hoặc Outlook và Banana gửi qua địa chỉ thật của bạn (không phải relay), nên phản hồi về đúng inbox và uy tín tên miền không bị ảnh hưởng. SMTP và Sendgrid/Resend/Postmark cũng được hỗ trợ ở gói trả phí." },
        { q: "Có hoạt động với ngôn ngữ khác ngoài tiếng Việt không?", a: "Có — soạn thảo và dịch hoạt động ở hơn 50 ngôn ngữ gồm tiếng Anh, Tây Ban Nha, Pháp, Đức, Ý, Bồ Đào Nha, Hà Lan, Trung, Nhật, Hàn, Thái, Ả Rập và Hindi. Viết prompt bằng bất kỳ ngôn ngữ nào bạn sẽ nhận lại bản nháp chất lượng bản địa." },
        { q: "Trình tạo miễn phí có giới hạn không?", a: "Đây là bản xem trước đầy đủ — bạn có thể chạy không giới hạn prompt trên trình duyệt. Lưu bản nháp, gửi từ inbox, A/B test, theo dõi mở/click và cá nhân hoá hàng loạt cần tài khoản Banana miễn phí." },
      ]}
      outroCta={{ to: "/signup", label: "Bắt đầu miễn phí" }}
      flow={{
        labels: {
          next: "Tiếp",
          back: "Quay lại",
          generate: "Viết ngay",
          step: "Bước {n} / {total}",
          nextThingsHeading: "Bạn còn có thể làm gì",
        },
        steps: [
          {
            question: "Loại email nào?",
            multi: false,
            options: ["Cold outreach", "Theo dõi (follow-up)", "Giới thiệu", "Xin lịch họp", "Đề xuất", "Kích hoạt lại", "Nhắc hoá đơn", "Cảm ơn"],
          },
          {
            question: "Tông giọng?",
            multi: false,
            options: ["Thân thiện", "Chuyên nghiệp", "Mạnh mẽ", "Ngắn gọn", "Thuyết phục", "Vui tươi"],
          },
          {
            question: "Độ dài?",
            multi: false,
            options: ["Ngắn (3 dòng)", "Vừa (1 đoạn)", "Dài (nhiều đoạn)"],
          },
        ],
        loadingMessages: [
          "Đang phân tích ý định của bạn…",
          "Đang nghiên cứu góc tiếp cận hiệu quả nhất…",
          "Đang soạn các biến thể tiêu đề…",
          "Đang viết câu mở đầu…",
          "Đang tinh chỉnh CTA…",
          "Đang A/B kiểm tra tông giọng…",
          "Đang trau chuốt phần chào cuối…",
        ],
        readyTitle: "Email của bạn đã sẵn sàng.",
        readyBody: "Banana AI đã viết một email cá nhân hoá cùng 3 biến thể tiêu đề. Đăng ký để xem, cá nhân hoá cho từng lead và gửi từ inbox của bạn.",
        ctaLabel: "Đăng ký để xem",
        ctaHref: "https://app.banana.cards/signup",
        nextThings: [
          { icon: Users, title: "Cá nhân hoá theo contact", desc: "Tự điền tên, công ty, ngành từ CRM của bạn." },
          { icon: Send, title: "Gửi từ tên miền của bạn", desc: "Gmail, Outlook, SMTP — uy tín của bạn, inbox của bạn." },
          { icon: LineChart, title: "Theo dõi mở & phản hồi", desc: "Xem ai đã mở, click và phản hồi theo thời gian thực." },
          { icon: Bookmark, title: "Lưu làm mẫu", desc: "Tái sử dụng bản nháp này cho các chiến dịch sau." },
        ],
      }}
    />
  );
}